import type { GranularityType } from "@tokenterminal/tt-analytics-api-types/dist/utils/granularity"
import type { Point } from "highcharts"
import { SERIE_STACK } from "../ChartOptions"
import type { Formatter } from "../sharedFormatter"
import type { TooltipOptions } from "../Tooltip"
import type { TooltipExpandedData, TooltipPointData } from "../types"
import { isOverlayPoint } from "./isOverlayPoint"

// Local replacements for Highcharts' `dateFormat` (UTC-based, zero-padded
// day, en-US names) so this module doesn't need a runtime Highcharts import.
const formatUTCLongDate = (timestamp: number): string =>
  new Date(timestamp).toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  })

const formatUTCMonthYear = (timestamp: number): string =>
  new Date(timestamp).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  })

const formatXValue = (
  value: number | string,
  granularity: GranularityType,
): { header: string; subheader: string | undefined } => {
  let header: string = ""
  let subheader: string | undefined

  if (typeof value === "string") {
    return { header: value, subheader: undefined }
  }

  const timestamp = value

  switch (granularity) {
    case "day": {
      // Thursday, October 23, 2025
      header = formatUTCLongDate(timestamp)
      break
    }
    case "week": {
      // Week from Monday, October 20, 2025
      header = `Week from ${formatUTCLongDate(timestamp)}`
      break
    }
    case "month": {
      // September 2025
      header = formatUTCMonthYear(timestamp)
      break
    }
    // Q1 2025
    // April - June
    case "quarter": {
      const startOfQuarter = new Date(timestamp)
      const endOfQuarter = new Date(startOfQuarter)
      endOfQuarter.setMonth(startOfQuarter.getUTCMonth() + 2)

      const quarterNumber = Math.floor((startOfQuarter.getUTCMonth() + 3) / 3)
      const year = startOfQuarter.getUTCFullYear()
      const startMonthName = startOfQuarter.toLocaleString("en-US", {
        month: "short",
        timeZone: "UTC",
      })
      const endMonthName = endOfQuarter.toLocaleString("en-US", {
        month: "short",
        timeZone: "UTC",
      })

      header = `Q${quarterNumber} ${year}`
      subheader = `${startMonthName} - ${endMonthName}`
      break
    }
    default:
      break
  }

  return {
    header,
    subheader,
  }
}

/**
 * Extracts complete tooltip data for all series at a given X position
 * Reuses the same logic as the tooltip formatter
 */
export function extractTooltipData(
  clickedPoint: Point,
  tooltipOptions: TooltipOptions = { sort: false, showTotal: true },
): TooltipExpandedData {
  const chart = clickedPoint.series.chart
  const x = clickedPoint.x

  // Not sure why we do this, but we do it in Tooltip.tsx too
  tooltipOptions.sort = true

  // Get all points at this X position across all series
  const allPointsAtX = chart.series
    .map((series) => series.points?.find((p) => p.x === x))
    .filter((p): p is Point => p !== undefined && p !== null)

  // Sort points if needed (same logic as Tooltip.tsx)
  const pointsToDisplay = [...allPointsAtX]
  if (tooltipOptions.sort) {
    pointsToDisplay.sort((a, b) => {
      // Pinned above the stack, matching the hover panel — an overlay is a
      // separate reading on its own axis, not the smallest band.
      const overlayOrder = Number(isOverlayPoint(b)) - Number(isOverlayPoint(a))
      if (overlayOrder !== 0) return overlayOrder
      const result = tooltipOptions.isLowerValueBetter
        ? (a.y ?? -1) - (b.y ?? -1)
        : (b.y ?? -1) - (a.y ?? -1)
      return result
    })
  }

  // Calculate aggregation (same logic as Tooltip.tsx)
  let totalValue: number | undefined
  if (tooltipOptions.showTotal) {
    const values: number[] = []
    pointsToDisplay.forEach((point) => {
      // Different unit, so it is not part of the sum — see the hover panel.
      if (isOverlayPoint(point)) return

      const isPercentageShare =
        // @ts-expect-error - stacking is a custom property we pass
        point.series.userOptions.stacking === SERIE_STACK.PERCENTAGE

      values.push(isPercentageShare ? point.percentage || 0 : point.y || 0)
    })

    if (tooltipOptions.aggregation === "median") {
      if (values.length > 0) {
        const sorted = [...values].sort((a, b) => a - b)
        const mid = Math.floor(sorted.length / 2)
        totalValue = sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!
      } else {
        totalValue = 0
      }
    } else {
      totalValue = values.reduce((s, v) => s + v, 0)
    }
  }

  const formattedPoints: TooltipPointData[] = pointsToDisplay.map((point) => {
    // Get specific formatter for the series
    const formatter = point.series.userOptions.custom?.formatValue as ReturnType<Formatter>

    return {
      seriesName: point.series.name,
      color: (point.color as string) || (point.series.color as string),
      y: point.y ?? 0,
      percentage: point.percentage,
      seriesIndex: point.series.index,
      formatter: formatter,
    }
  })

  let header: string | undefined = ""
  let subheader: string | undefined

  if (tooltipOptions.headerOverride) {
    header = tooltipOptions.headerOverride
  } else {
    const formattedXValue = formatXValue(
      x,
      clickedPoint.series.userOptions.custom?.granularity as GranularityType,
    )
    header = formattedXValue.header
    subheader = formattedXValue.subheader
  }

  return {
    header,
    subheader,
    points: formattedPoints,
    total: totalValue,
    totalFormatter: pointsToDisplay.find((point) => !isOverlayPoint(point))?.series.userOptions
      .custom?.formatValue as ReturnType<Formatter> | undefined,
    showTotal: tooltipOptions.showTotal ?? false,
    totalLabel: tooltipOptions.aggregationLabel ?? "Total",
    isSorted: tooltipOptions.sort ?? false,
  }
}
